/**
 * Panel 0: Logistics — Fuel on left, Ordnance on right, Sustainability at bottom
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import type {
  TheaterFuelSummary,
  TheaterOrdnanceSummary,
  SustainabilityResult,
} from '../../../types'
import { DOMAIN_CONFIG, type Domain } from '../../../types'
import {
  getTheaterFuel,
  getTheaterOrdnance,
  calcSustainability,
  updateOrdnanceOnHand,
  formatPercent,
} from '../../../services/api'
import { FuelGauge } from './FuelGauge'
import { SupplyBar } from './SupplyBar'

interface LogisticsPanelProps {
  theaterVersion?: number
  onTheaterChange?: () => void
}

export function LogisticsPanel({ theaterVersion, onTheaterChange }: LogisticsPanelProps) {
  const [fuelData, setFuelData] = useState<TheaterFuelSummary | null>(null)
  const [ordnanceData, setOrdnanceData] = useState<TheaterOrdnanceSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Sustainability calculator
  const [sortiesInput, setSortiesInput] = useState(20)
  const [sustainResult, setSustainResult] = useState<SustainabilityResult | null>(null)
  const [calcLoading, setCalcLoading] = useState(false)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [fuelRes, ordRes] = await Promise.all([getTheaterFuel(), getTheaterOrdnance()])
      if (fuelRes.error) throw new Error(fuelRes.error)
      if (ordRes.error) throw new Error(ordRes.error)
      if (fuelRes.data) setFuelData(fuelRes.data)
      if (ordRes.data) setOrdnanceData(ordRes.data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch logistics data')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchData() }, [fetchData, theaterVersion])

  const runSustainability = useCallback(async (sorties: number) => {
    setCalcLoading(true)
    const res = await calcSustainability(sorties)
    if (res.data) setSustainResult(res.data)
    setCalcLoading(false)
  }, [])

  // Inline ordnance editing
  const [editingMunition, setEditingMunition] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')
  const [toast, setToast] = useState<string | null>(null)

  const handleOrdnanceEdit = useCallback(async (munitionId: string, oldOnHand: number) => {
    const newOnHand = parseInt(editValue)
    if (isNaN(newOnHand) || newOnHand < 0 || newOnHand === oldOnHand) {
      setEditingMunition(null)
      return
    }
    const res = await updateOrdnanceOnHand(munitionId, newOnHand)
    setEditingMunition(null)
    if (res.data) {
      // Find the short name
      const item = ordnanceData?.stockpile.find(s => s.munition_id === munitionId)
      const name = item?.short_name || munitionId
      setToast(`Updated ${name}: ${oldOnHand.toLocaleString()} → ${newOnHand.toLocaleString()}`)
      setTimeout(() => setToast(null), 3000)
      fetchData()
      onTheaterChange?.()
    }
  }, [editValue, ordnanceData, fetchData, onTheaterChange])

  // Ordnance table sorting
  const [ordSort, setOrdSort] = useState<'munition' | 'on_hand' | 'days' | 'status'>('days')
  const [ordSortAsc, setOrdSortAsc] = useState(true)

  const sortedStockpile = useMemo(() => {
    if (!ordnanceData) return []
    const arr = [...ordnanceData.stockpile]
    arr.sort((a, b) => {
      let cmp = 0
      switch (ordSort) {
        case 'munition': cmp = a.munition_name.localeCompare(b.munition_name); break
        case 'on_hand': cmp = a.on_hand - b.on_hand; break
        case 'days': cmp = a.days_of_supply - b.days_of_supply; break
        case 'status': cmp = a.status.localeCompare(b.status); break
      }
      return ordSortAsc ? cmp : -cmp
    })
    return arr
  }, [ordnanceData, ordSort, ordSortAsc])

  const handleOrdSort = (key: typeof ordSort) => {
    if (ordSort === key) setOrdSortAsc(!ordSortAsc)
    else { setOrdSort(key); setOrdSortAsc(true) }
  }

  const sortInd = (key: typeof ordSort) => ordSort === key ? (ordSortAsc ? ' ▲' : ' ▼') : ''

  if (loading) {
    return (
      <div className="flex flex-col gap-4 animate-pulse">
        <div className="flex gap-4">
          {[1,2,3,4].map(i => <div key={i} className="flex-1 h-16 bg-term-bgLight rounded border border-term-border/30" />)}
        </div>
        <div className="h-80 bg-term-bgLight rounded border border-term-border/30" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <div className="text-term-error text-lg font-semibold">Error loading logistics</div>
        <div className="text-term-textDim text-sm">{error}</div>
        <button onClick={fetchData} className="term-button term-button-primary">Retry</button>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4 h-full overflow-auto">
      {/* Toast notification */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 px-4 py-2 bg-emerald-500/20 border border-emerald-500/40 rounded text-xs text-emerald-400 font-medium shadow-lg">
          ✓ {toast}
        </div>
      )}

      {/* Theater Summary Stats */}
      {fuelData && ordnanceData && (
        <div className="grid grid-cols-5 gap-3">
          <StatCard label="Theater Fuel" value={formatPercent(fuelData.theater_fuel_pct)} icon="⛽" highlight={fuelData.theater_fuel_pct < 0.5} />
          <StatCard label="Fuel Days" value={`${fuelData.days_of_supply}d`} icon="⏱" highlight={fuelData.days_of_supply < 5} />
          <StatCard label="Total Munitions" value={ordnanceData.total_munitions.toLocaleString()} icon="💣" />
          <StatCard label="Critical Low" value={String(ordnanceData.critical_shortfalls)} icon="⚠" highlight={ordnanceData.critical_shortfalls > 0} />
          <StatCard
            label="Fuel Status"
            value={`${fuelData.assets_green}G ${fuelData.assets_amber}A ${fuelData.assets_red}R ${fuelData.assets_black}B`}
            icon="●"
          />
        </div>
      )}

      {/* Main content: Fuel left, Ordnance right */}
      <div className="grid grid-cols-2 gap-4 flex-1 min-h-0">
        {/* LEFT: Theater Fuel Dashboard */}
        <div className="term-box p-3 overflow-auto">
          <div className="text-xs text-term-textDim font-medium uppercase tracking-wider mb-3">
            Theater Fuel Status
          </div>

          {/* Theater fill bar */}
          {fuelData && (
            <div className="mb-4">
              <FuelGauge fuelPct={fuelData.theater_fuel_pct} status={fuelData.theater_fuel_pct >= 0.6 ? 'GREEN' : fuelData.theater_fuel_pct >= 0.3 ? 'AMBER' : 'RED'} />
              <div className="flex justify-between text-[10px] text-term-textDim mt-1">
                <span>On hand: {(fuelData.total_on_hand_lbs / 1e6).toFixed(1)}M lbs</span>
                <span>Capacity: {(fuelData.total_capacity_lbs / 1e6).toFixed(1)}M lbs</span>
              </div>
              <div className="text-[10px] text-term-textDim mt-0.5">
                Daily burn (12h ops): {(fuelData.daily_burn_rate_lbs / 1e6).toFixed(2)}M lbs/day
              </div>
            </div>
          )}

          {/* Per-asset fuel gauges grouped by domain */}
          {fuelData && (['AIR', 'SEA'] as Domain[]).map(domain => {
            const domainAssets = fuelData.fuel_states.filter(s => s.domain === domain)
            if (domainAssets.length === 0) return null
            return (
              <div key={domain} className="mb-3">
                <div className="text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: DOMAIN_CONFIG[domain].color }}>
                  {domain} ({domainAssets.length})
                </div>
                <div className="space-y-1.5">
                  {domainAssets.map(s => (
                    <div key={s.asset_id} className="flex items-center gap-2">
                      <span className="text-[10px] text-term-text truncate w-32 flex-shrink-0" title={s.asset_type}>
                        {s.asset_type.split(' ')[0]}
                      </span>
                      <FuelGauge fuelPct={s.fuel_pct} status={s.status} timeToBingo={s.time_to_bingo_hrs} compact />
                      <span className="text-[9px] text-term-textDim tabular-nums w-10 text-right flex-shrink-0">
                        {s.sorties_remaining}sr
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </div>

        {/* RIGHT: Ordnance Stockpile Table */}
        <div className="term-box p-3 overflow-auto">
          <div className="text-xs text-term-textDim font-medium uppercase tracking-wider mb-3">
            Theater Ordnance Stockpile
          </div>

          <table className="term-table text-xs w-full">
            <thead className="sticky top-0 bg-term-bg z-10">
              <tr>
                <th className="text-left cursor-pointer select-none hover:text-term-text" onClick={() => handleOrdSort('munition')}>Munition{sortInd('munition')}</th>
                <th className="text-center">Cat</th>
                <th className="text-right cursor-pointer select-none hover:text-term-text" onClick={() => handleOrdSort('on_hand')}>On Hand{sortInd('on_hand')}</th>
                <th className="text-right">Daily Rate</th>
                <th className="text-right cursor-pointer select-none hover:text-term-text" onClick={() => handleOrdSort('days')}>Days Supply{sortInd('days')}</th>
                <th className="text-center cursor-pointer select-none hover:text-term-text" onClick={() => handleOrdSort('status')}>Status{sortInd('status')}</th>
              </tr>
            </thead>
            <tbody>
              {sortedStockpile.map(s => {
                const statusColor = s.status === 'GREEN' ? '#22c55e' : s.status === 'AMBER' ? '#f59e0b' : '#ef4444'
                return (
                  <tr key={s.munition_id}>
                    <td className="text-left font-bold text-term-text whitespace-nowrap">{s.short_name}</td>
                    <td className="text-center">
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-term-bgLight text-term-textDim">
                        {s.category}
                      </span>
                    </td>
                    <td className="text-right tabular-nums">
                      {editingMunition === s.munition_id ? (
                        <input
                          type="number"
                          min={0}
                          autoFocus
                          value={editValue}
                          onChange={e => setEditValue(e.target.value)}
                          onBlur={() => handleOrdnanceEdit(s.munition_id, s.on_hand)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') handleOrdnanceEdit(s.munition_id, s.on_hand)
                            if (e.key === 'Escape') setEditingMunition(null)
                          }}
                          className="w-20 px-1 py-0.5 text-xs bg-term-bgDark border border-term-primary rounded text-term-text text-right tabular-nums focus:outline-none"
                        />
                      ) : (
                        <span
                          className="cursor-pointer hover:text-term-primary hover:underline"
                          onClick={() => { setEditingMunition(s.munition_id); setEditValue(String(s.on_hand)) }}
                          title="Click to edit"
                        >
                          {s.on_hand.toLocaleString()}
                        </span>
                      )}
                    </td>
                    <td className="text-right tabular-nums text-term-textDim">{s.daily_expenditure_rate}/day</td>
                    <td className="text-right">
                      <SupplyBar daysOfSupply={s.days_of_supply} compact />
                    </td>
                    <td className="text-center">
                      <span className="text-[10px] font-bold" style={{ color: statusColor }}>{s.status}</span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>

          {/* Critical shortfall callout */}
          {ordnanceData && ordnanceData.critical_shortfalls > 0 && (
            <div className="mt-3 p-2 bg-red-500/10 border border-red-500/30 rounded text-[11px]">
              <span className="text-red-400 font-bold">CRITICAL: </span>
              <span className="text-term-textDim">
                {ordnanceData.stockpile.filter(s => s.status === 'RED').map(s => s.short_name).join(', ')} below 3-day supply threshold
              </span>
            </div>
          )}

          {/* Compatibility summary */}
          {ordnanceData && ordnanceData.loadouts.length > 0 && (
            <div className="mt-3">
              <div className="text-[10px] text-term-textDim font-medium uppercase tracking-wider mb-1">
                Armed Platforms ({ordnanceData.loadouts.length})
              </div>
              <div className="grid grid-cols-3 gap-1">
                {ordnanceData.loadouts.map(lo => (
                  <div key={lo.asset_id} className="text-[10px] term-box p-1.5">
                    <div className="font-bold text-term-text truncate">{lo.asset_type.split(' ')[0]}</div>
                    <div className="text-term-textDim">{lo.stations_used}/{lo.stations_total} ord | {(lo.current_weight_lbs/1000).toFixed(0)}k lbs</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom: Sustainability Calculator */}
      <div className="term-box p-3">
        <div className="text-xs text-term-textDim font-medium uppercase tracking-wider mb-2">
          Sustainability Calculator
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-xs text-term-textDim">Sorties/Day:</label>
            <input
              type="number"
              min={0}
              max={200}
              value={sortiesInput}
              onChange={e => setSortiesInput(Math.max(0, Number(e.target.value)))}
              className="w-20 px-2 py-1 text-xs bg-term-bgDark border border-term-border rounded text-term-text text-right tabular-nums focus:outline-none focus:border-term-primary"
            />
          </div>

          {/* Quick presets */}
          {[
            { label: 'STEADY', val: 15 },
            { label: 'SURGE', val: 40 },
            { label: 'MAX', val: 80 },
          ].map(p => (
            <button
              key={p.label}
              onClick={() => { setSortiesInput(p.val); runSustainability(p.val) }}
              className="px-2 py-1 text-[10px] font-bold uppercase rounded border border-term-border text-term-textDim hover:border-term-primary/50 hover:text-term-text transition-colors"
            >
              {p.label} ({p.val})
            </button>
          ))}

          <button
            onClick={() => runSustainability(sortiesInput)}
            disabled={calcLoading}
            className="px-3 py-1 text-[10px] font-bold uppercase rounded bg-term-primary/20 text-term-primary border border-term-primary/40 hover:bg-term-primary/30 transition-colors disabled:opacity-40"
          >
            {calcLoading ? 'Calculating...' : 'Calculate'}
          </button>
        </div>

        {sustainResult && (
          <div className="mt-3 grid grid-cols-4 gap-3">
            <div className="term-box p-2 text-center">
              <div className="text-[9px] text-term-textDim uppercase">Fuel Sustains</div>
              <div className="text-lg font-bold tabular-nums" style={{ color: sustainResult.fuel_days > 7 ? '#22c55e' : sustainResult.fuel_days > 3 ? '#f59e0b' : '#ef4444' }}>
                {sustainResult.fuel_days}d
              </div>
            </div>
            <div className="term-box p-2 text-center">
              <div className="text-[9px] text-term-textDim uppercase">Ordnance Sustains</div>
              <div className="text-lg font-bold tabular-nums" style={{ color: sustainResult.ordnance_days > 7 ? '#22c55e' : sustainResult.ordnance_days > 3 ? '#f59e0b' : '#ef4444' }}>
                {sustainResult.ordnance_days}d
              </div>
            </div>
            <div className="term-box p-2 text-center">
              <div className="text-[9px] text-term-textDim uppercase">Limiting Factor</div>
              <div className={`text-sm font-bold ${sustainResult.limiting_factor === 'FUEL' ? 'text-term-warning' : 'text-term-error'}`}>
                {sustainResult.limiting_factor}
              </div>
            </div>
            <div className="term-box p-2 text-center">
              <div className="text-[9px] text-term-textDim uppercase">Limiting Item</div>
              <div className="text-xs font-bold text-term-text truncate" title={sustainResult.limiting_item}>
                {sustainResult.limiting_item}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function StatCard({ label, value, icon, highlight }: { label: string; value: string; icon: string; highlight?: boolean }) {
  return (
    <div className={`term-box p-3 flex items-center gap-3 ${highlight ? 'border-term-warning/50 glow-warning' : ''}`}>
      <div className="text-xl text-term-textDim opacity-50">{icon}</div>
      <div>
        <div className="text-[10px] text-term-textDim uppercase tracking-wider">{label}</div>
        <div className="text-lg font-bold text-term-text tabular-nums">{value}</div>
      </div>
    </div>
  )
}
